import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, capitalize } from 'lodash';
import moment from 'moment';
import { SectionHeading } from 'web-ui-components/lib/typography/headings';
import { ProminentLabel } from 'web-ui-components/lib/typography/labels';
import { PaymentReview } from '../../../sharedComponents/PaymentReview/PaymentReview';
import paymentPropTypes from '../../../states/payments/propTypes';
import withContent from '../../../containers/withContent';
import confirmedContent, { contentPropTypes } from './confirmed.content';
import {
	getConfirmedBatchPayments,
	getConfirmedBatchTotal,
	getFirstPaymentIsFutureDated,
} from '../../../states/payments/selectors';
import { TotalBar } from '../../../sharedComponents/TotalBar/TotalBar';
import {
	getBatchAuthorisationType,
	getBatchConfirmationStatus,
	getBatchName,
	isConfirmedDailyLimit,
	getBatchPaymentError,
	getBatchPaymentsStatus,
	isUserDeviceNotEnrolled,
	getBatchAuthorisers,
	isBatchTypeUpload,
} from '../../../states/batch/selectors';
import { dateFormat, FASTER_PAYMENTS_BATCH_PENDING_STATUS_ERROR_CODE } from '../../../config/';
import ApiConstants from '../../../config/apiConstants';
import SubmittedTransactionTable from '../../../sharedComponents/SubmittedTransactionTable/SubmittedTransactionTable';
import { failedPaymentStyles, obligatePaymentStyles } from './ConfirmedBatchStyles';
import { isUserPaymentAuthoriser, getAuthorisationMessage } from '../../../helpers/fieldHelpers';
import { getUserId } from '../../../states/user/selectors';

const mapState = (state) => ({
	payments: getConfirmedBatchPayments(state),
	batchTotal: getConfirmedBatchTotal(state),
	batchAuthType: getBatchAuthorisationType(state),
	batchStatus: getBatchConfirmationStatus(state),
	batchName: getBatchName(state),
	isConfirmedDailyLimit: isConfirmedDailyLimit(state),
	isUserDeviceNotEnrolled: isUserDeviceNotEnrolled(state),
	paymentError: getBatchPaymentError(state),
	isBatchFutureDated: getFirstPaymentIsFutureDated(state),
	batchPaymentsStatusList: getBatchPaymentsStatus(state),
	authorisers: getBatchAuthorisers(state),
	userId: getUserId(state),
	isBatchTypeUpload: isBatchTypeUpload(state),
});

const shouldShowIndividualPaymentStatus = (status) => status === ApiConstants.accept;
const getIndividualPaymentStatus = (batchPaymentsStatusList, paymentId) =>
	batchPaymentsStatusList.find(({ id }) => id === paymentId) || batchPaymentsStatusList[0];

const getIndividualPaymentSidebarTitle = (paymentStatus, isFutureDated, content, code) => {
	let status = paymentStatus;
	if (code === FASTER_PAYMENTS_BATCH_PENDING_STATUS_ERROR_CODE) {
		status = 'pending';
	}
	switch (status) {
		case 'success':
			if (isFutureDated) return content.fdpScheduledHeader;
			return content.paymentSubmittedHeader;
		case 'pending':
			return content.batchPendingHeader;
		case 'failed':
			return content.batchErrorHeader;
		default:
			return '';
	}
};

const ConfirmedBatch = ({
	payments = [],
	batchTotal,
	batchAuthType,
	content,
	batchStatus,
	batchName,
	handleRoute,
	isConfirmedDailyLimit,
	paymentError,
	batchPaymentsStatusList,
	isBatchFutureDated,
	isUserDeviceNotEnrolled,
	authorisers,
	userId,
	isBatchTypeUpload,
}) => {
	const getDate = ({ date }) => {
		if (moment.isMoment(date)) return date.format(dateFormat);
		return '';
	};

	const batchSoftTokenSideBarDisplay = isBatchTypeUpload && batchAuthType === ApiConstants.softToken;

	const getSideBarforFileUpload = () => {
		const {
			fileUploadFirstInstruction,
			fileUploadSecondInstruction,
			fileUploadThirdInstruction,
			fileUploadFourthInstruction,
			fileUploadFifthInstruction,
		} = content;
		return (
			<center>
				<p>{fileUploadFirstInstruction}</p>
				<ProminentLabel>{fileUploadSecondInstruction}</ProminentLabel>
				<p>{fileUploadThirdInstruction}</p>
				<ProminentLabel>{fileUploadFourthInstruction}</ProminentLabel>
				<p>{fileUploadFifthInstruction}</p>
			</center>
		);
	};

	const getSidebarBody = (type, content, hardTokenAuthRequired) => {
		switch (type) {
			case ApiConstants.obligate:
				if (!isUserPaymentAuthoriser(authorisers, userId) || authorisers.length > 1) {
					return batchSoftTokenSideBarDisplay
						? getSideBarforFileUpload()
						: getAuthorisationMessage(authorisers, userId);
				}
				if (hardTokenAuthRequired) return content.batchAuthorisationHardTokenSidebarBody;
				return batchSoftTokenSideBarDisplay
					? getSideBarforFileUpload()
					: content.batchAuthorisationSoftTokenSidebarBody;
			case ApiConstants.decline:
				if (isConfirmedDailyLimit) return content.batchDailyLimitErrorBody;
				if (paymentError) return paymentError.message;
				return '';
			default:
				return '';
		}
	};

	const getStatus = (status) => {
		switch (status) {
			case ApiConstants.accept:
				return 'success';
			case ApiConstants.obligate:
				return 'warning';
			default:
				return '';
		}
	};

	const getSidebarIcon = (type) => {
		switch (type) {
			case ApiConstants.obligate:
				return 'AuthorisationLarge';
			case ApiConstants.decline:
				return 'CrossOutlineLarge';
			default:
				return '';
		}
	};

	const getSidebarTitle = (type, content) => {
		switch (type) {
			case ApiConstants.obligate:
				return batchSoftTokenSideBarDisplay
					? content.fileUploadTwoStepInstructionHeading
					: content.batchAuthorisationSidebarTitle;
			case ApiConstants.decline:
				return content.batchErrorHeader;
			default:
				return '';
		}
	};

	const getHeader = ({ batchHeaderNoAuthRequired, batchHeaderAuthRequired }, requiresAuthentication) => {
		if (requiresAuthentication) {
			return `${batchName} ${batchHeaderAuthRequired}`;
		}

		return `${batchName} ${batchHeaderNoAuthRequired}`;
	};
	const getSidebarType = (isDailyLimit, status, isUserDeviceNotEnrolled) => {
		if (status === ApiConstants.obligate) {
			return status;
		} else if (isDailyLimit || status === ApiConstants.decline || isUserDeviceNotEnrolled) {
			return ApiConstants.decline;
		}
		return '';
	};

	const getShowSidebar = (isDailyLimit, status, isUserDeviceNotEnrolled) =>
		status === ApiConstants.obligate || status === ApiConstants.decline || isDailyLimit || isUserDeviceNotEnrolled;

	const {
		fromLabel,
		toLabel,
		amtLabel,
		refLabel,
		dateLabel,
		typeLabel,
		batchAuthorisationSidebarButtonText,
		fileUploadReviewButton,
	} = content;

	const paymentLabels = {
		fromLabel,
		toLabel,
		amtLabel,
		refLabel,
		dateLabel,
		typeLabel,
	};
	const requiresAuthentication = batchStatus === ApiConstants.obligate || isUserDeviceNotEnrolled;
	const showSidebar = getShowSidebar(isConfirmedDailyLimit, batchStatus, isUserDeviceNotEnrolled);
	const sidebarType = getSidebarType(isConfirmedDailyLimit, batchStatus, isUserDeviceNotEnrolled);
	const hardTokenAuthRequired = batchAuthType === 'HARD';
	const header = getHeader(content, requiresAuthentication);
	const status = getStatus(batchStatus);
	const sidebarContent = getSidebarBody(sidebarType, content, hardTokenAuthRequired);
	const sidebarIconType = getSidebarIcon(sidebarType);
	const sidebarTitle = getSidebarTitle(sidebarType, content);
	const showIndividualPaymentStatus = shouldShowIndividualPaymentStatus(batchStatus);

	const getPaymentRow = (paymentRow) => {
		if (requiresAuthentication) {
			return paymentRow;
		}
		return <div className={failedPaymentStyles}>{paymentRow}</div>;
	};
	const getAllPayments = (payments) =>
		payments.map((payment) => {
			const individualPaymentStatus = getIndividualPaymentStatus(batchPaymentsStatusList, payment.id);
			const statusTitle = getIndividualPaymentSidebarTitle(
				get(individualPaymentStatus, 'status', ''),
				isBatchFutureDated,
				content,
				get(individualPaymentStatus, 'code', '')
			);
			const paymentRow = (
				<div className={obligatePaymentStyles}>
					<PaymentReview
						key={payment.id}
						fromAccountName={get(payment, 'fromAccountDetails.displayName', '')}
						fromAccountSortCode={get(payment, 'fromAccountDetails.sortCode', '')}
						fromAccountNumber={get(payment, 'fromAccountDetails.accountNumber', '')}
						fromAccountHolder={get(payment, 'fromAccountDetails.metadata.account_name', '')}
						toAccountName={get(payment, 'toAccountDetails.displayName', '')}
						toAccountSortCode={get(payment, 'toAccountDetails.sortCode', '')}
						toAccountNumber={get(payment, 'toAccountDetails.accountNumber', '')}
						amount={payment.amount}
						reference={payment.reference}
						paymentDate={getDate(payment)}
						paymentType={payment.type === 'single' ? 'One-off' : capitalize(payment.type)}
						status={get(individualPaymentStatus, 'status', '')}
						statusTitle={statusTitle}
						statusSubTitle={get(individualPaymentStatus, 'statusMessage', '')}
						labels={paymentLabels}
						hideStatus={!showIndividualPaymentStatus}
						hasStatus
					/>
				</div>
			);
			return getPaymentRow(paymentRow);
		});
	const getTransactionsTableComponent = () => {
		const sidebarButtonText = batchSoftTokenSideBarDisplay
			? fileUploadReviewButton
			: batchAuthorisationSidebarButtonText;
		const sidebarShowButton = isBatchTypeUpload
			? isBatchTypeUpload && isUserPaymentAuthoriser(authorisers, userId)
			: hardTokenAuthRequired;
		if (requiresAuthentication) {
			return (
				<SubmittedTransactionTable
					paymentsTotal={batchTotal}
					showSidebar={showSidebar}
					sidebarIconType={sidebarIconType}
					sidebarTitle={sidebarTitle}
					sidebarContent={sidebarContent}
					sidebarShowButton={sidebarShowButton}
					sidebarButtonOnClick={() => handleRoute('WEB-MANAGE-PAYMENT-STATUS')}
					sidebarButtonIcon="ChevronRightSolidSmall"
					sidebarButtonText={sidebarButtonText}
					labels={paymentLabels}
					authorisers={authorisers}
				>
					{getAllPayments(payments)}
				</SubmittedTransactionTable>
			);
		}
		return getAllPayments(payments);
	};

	return (
		<div>
			<SectionHeading>{header}</SectionHeading>
			{getTransactionsTableComponent()}
			{!requiresAuthentication && <TotalBar total={batchTotal} />}
		</div>
	);
};

ConfirmedBatch.propTypes = {
	payments: PropTypes.arrayOf(PropTypes.shape(paymentPropTypes)).isRequired,
	batchTotal: PropTypes.string,
	handleRoute: PropTypes.func.isRequired,
	batchAuthType: PropTypes.string,
	content: PropTypes.shape(contentPropTypes),
	batchStatus: PropTypes.string.isRequired,
	batchName: PropTypes.string.isRequired,
	isConfirmedDailyLimit: PropTypes.bool.isRequired,
	paymentError: PropTypes.shape({}),
	batchPaymentsStatusList: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			code: PropTypes.string.isRequired,
			statusMapping: PropTypes.string.isRequired,
		})
	).isRequired,
	isUserDeviceNotEnrolled: PropTypes.bool.isRequired,
	isBatchFutureDated: PropTypes.bool.isRequired,
	authorisers: PropTypes.arrayOf(PropTypes.shape({})),
	userId: PropTypes.string.isRequired,
	isBatchTypeUpload: PropTypes.bool,
};

const contentWrapper = withContent({ content: confirmedContent });
export default connect(mapState)(contentWrapper(ConfirmedBatch));
export const ConnectWrapper = connect(mapState)(ConfirmedBatch);
